import { useEffect, useState } from 'react';
import {
  Stack, PasswordInput, Button, Group, Text, Divider, SegmentedControl, Avatar, FileButton, Anchor,
  TextInput, Textarea, Alert, CopyButton, ActionIcon, Tooltip, Center, Loader, Modal,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  IconCopy, IconCheck, IconExternalLink, IconRefresh, IconTrash, IconDeviceTv,
  IconAlertTriangle, IconDeviceFloppy, IconFileText, IconUser, IconLanguage, IconLock, IconScale, IconPencil,
} from '@tabler/icons-react';
import api, { fileUrl } from '../services/api';
import { SectionCard } from '../components/SectionCard';
import { WorkDaysSettings } from '../components/WorkDaysSettings';
import { LessonTimeSettings } from '../components/LessonTimeSettings';
import { BackupSettings } from '../components/BackupSettings';
import { PageHeader } from '../components/PageHeader';
import { SupportEmailSettings } from '../components/SupportEmailSettings';
import { DebtStartSettings } from '../components/DebtStartSettings';
import { PayrollRuleSettings } from '../components/PayrollRuleSettings';
import { AccountDeletionRequests } from '../components/AccountDeletionRequests';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformSuperadmin, isTenantStaff, STAFF_ROLES } from '../lib/permissions';
import { LOGIN_MIN, FULL_NAME_MIN, FULL_NAME_MAX, cleanFullName, fullNameError } from '../lib/password';
import type { AuthUser } from '../services/authService';
import { SILENT_ERROR } from '../lib/queryMeta';
import { setLanguage } from '../i18n';
import { Lang } from '../i18n/locales';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;


// ─────────────────────────────────────────────────────────────
//  Shartnoma rekvizitlari (GET/PUT /contracts/requisites)
// ─────────────────────────────────────────────────────────────

/** Backend javobi (`routes/contracts.ts` → `/requisites`). */
interface RequisitesResponse {
  requisites: Record<string, string>;
  /** Majburiy maydonlar RO'YXATI serverdan keladi — forma shu bo'yicha quriladi. */
  requiredKeys?: string[];
  optionalKeys?: string[];
  /** `false` — shartnoma hujjati umuman yaratilmaydi. */
  complete: boolean;
  missing?: string[];
  /** `true` — qiymatlar hali SAQLANMAGAN, faqat taklif (admin tasdiqlashi kerak). */
  isDefault?: boolean;
}

// Server ro'yxat yubormasa (eski backend) — zaxira tartib. `lib/contract.ts` bilan bir xil.
const REQUIRED_FALLBACK = ['schoolName', 'address', 'director', 'stir', 'bank', 'account', 'mfo', 'phone'];
const OPTIONAL_FALLBACK = ['directorShort', 'jurist', 'city'];

/** Bitta maydon uzunligi chegarasi (backend `REQUISITE_MAX_LEN`). */
const REQUISITE_MAX_LEN = 300;

/** Qo'shimcha izoh ko'rsatiladigan maydonlar (qolganlariga sarlavhaning o'zi yetarli). */
const DESCRIBED_KEYS = ['schoolName', 'directorShort', 'jurist', 'city'];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useAuth();
  const qc = useQueryClient();
  // Kiosk havolasi: ochiq TV ekrani maktab reytingi, jadvali va o'quvchi fotolarini
  // ko'rsatgani uchun `/kiosk/admin/key` MANAGERS emas, STAFF talab qiladi —
  // ACCOUNTANT/RECEPTION 403 oladi. Platforma superadminining maktabi yo'q (doim 400),
  // shuning uchun bo'lim faqat maktab darajasidagi STAFF rollariga ko'rsatiladi.
  const canManageKiosk = isTenantStaff(user);
  // Support email (`/tenant/support-email` PUT — backend STAFF) — xuddi shu qoida.
  const canManageSupportEmail = isTenantStaff(user);
  /* Ish kunlari — maoshga ta'sir qiladi, shuning uchun doira TOR: direktor va
     buxgalter. Oddiy o'qituvchi buni o'zgartira olsa, butun maktabning davomati
     va maosh hisobi bitta bosishda buzilardi. */
  const canManageWorkDays =
    ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT'].includes(user?.role ?? '') && !isPlatformSuperadmin(user);

  /* Profilni tahrirlash (F.I.Sh. + login) — faqat boshqaruv (STAFF: platforma superadmini,
     direktor, admin). Backend: PATCH /auth/me {fullName}, POST /auth/change-login
     {login, currentPassword}. Boshqa ichki rollarda (o'qituvchi, buxgalter, qabulxona)
     karta ko'rish rejimida qoladi — ularning ma'lumotini admin `/staff` da tahrirlaydi. */
  const canEditProfile = STAFF_ROLES.includes(user?.role ?? '');
  const [profileOpen, setProfileOpen] = useState(false);
  const profileForm = useForm({
    initialValues: { fullName: '', login: '', currentPassword: '' },
    validate: {
      fullName: (v) => { const k = fullNameError(v); return k ? t(`settings.${k}`, { n: k === 'nameMax' ? FULL_NAME_MAX : FULL_NAME_MIN }) : null; },
      login: (v) => (v.trim().length < LOGIN_MIN ? t('settings.loginMin', { n: LOGIN_MIN }) : null),
      currentPassword: (v, values) =>
        values.login.trim() !== (user?.login ?? '') && !v ? t('settings.currentPasswordRequired') : null,
    },
  });
  const loginChanged = profileForm.values.login.trim() !== (user?.login ?? '');
  const openProfile = () => {
    const init = { fullName: user?.fullName ?? '', login: user?.login ?? '', currentPassword: '' };
    profileForm.setValues(init);
    profileForm.resetDirty(init);
    profileForm.clearErrors();
    setProfileOpen(true);
  };

  const profileMut = useMutation({
    mutationFn: async (v: typeof profileForm.values) => {
      const login = v.login.trim();
      const fullName = cleanFullName(v.fullName);
      let me: AuthUser | null = null;
      // Avval login (joriy parol tekshiriladi — xato bo'lsa hech narsa o'zgarmaydi), keyin ism
      if (login !== user?.login) {
        me = (await api.post<AuthUser>('/auth/change-login', { login, currentPassword: v.currentPassword })).data;
        updateUser({ login: me.login });
      }
      if (fullName !== user?.fullName) {
        me = (await api.patch<AuthUser>('/auth/me', { fullName })).data;
        updateUser({ fullName: me.fullName });
      }
      return me;
    },
    onSuccess: (me) => {
      notifications.show({ color: 'green', message: me ? t('settings.profileSaved') : t('settings.nothingChanged') });
      setProfileOpen(false);
      profileForm.reset();
    },
    onError: (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('common.error')) }),
  });

  const avatarMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<{ avatar: string }>('/auth/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (data) => {
      updateUser({ avatar: data.avatar });
      notifications.show({ color: 'green', message: t('settings.avatarUpdated') });
    },
  });

  const avatarDelMut = useMutation({
    mutationFn: () => api.delete('/auth/avatar'),
    onSuccess: () => {
      updateUser({ avatar: null });
      notifications.show({ color: 'green', message: t('settings.avatarRemoved') });
    },
  });

  const form = useForm({
    initialValues: { oldPassword: '', newPassword: '', confirm: '' },
    validate: {
      newPassword: (v) => (v.length < 5 ? t('settings.min5') : null),
      confirm: (v, values) => (v !== values.newPassword ? t('settings.mismatch') : null),
    },
  });

  const changeMut = useMutation({
    mutationFn: (v: { oldPassword: string; newPassword: string }) => api.post('/auth/change-password', v),
    onSuccess: () => { notifications.show({ color: 'green', message: t('settings.changed') }); form.reset(); },
  });

  // ── Kiosk ekrani (TV) havolasi ──
  const { data: kiosk, isLoading: kioskLoading, isError: kioskIsError, refetch: refetchKiosk, isFetching: kioskFetching } = useQuery({
    queryKey: ['kiosk-key'],
    queryFn: async () => (await api.get<{ kioskKey: string | null }>('/kiosk/admin/key')).data,
    enabled: canManageKiosk,
    meta: SILENT_ERROR, // xato bo'limning o'zida ochiq ko'rsatiladi
  });
  const kioskUrl = kiosk?.kioskKey ? `${window.location.origin}/kiosk?key=${kiosk.kioskKey}` : '';
  const invalidateKiosk = () => qc.invalidateQueries({ queryKey: ['kiosk-key'] });
  const kioskErr = (e: unknown) => notifications.show({ color: 'red', message: errMsg(e, t('common.error')) });

  const kioskKeyMut = useMutation({
    mutationFn: async (_mode: 'create' | 'regenerate') =>
      (await api.post<{ kioskKey: string }>('/kiosk/admin/key')).data,
    onSuccess: (_data, mode) => {
      invalidateKiosk();
      notifications.show({ color: 'green', message: mode === 'create' ? t('kiosk.created') : t('kiosk.regenerated') });
    },
    onError: kioskErr,
  });
  const kioskDelMut = useMutation({
    mutationFn: () => api.delete('/kiosk/admin/key'),
    onSuccess: () => { invalidateKiosk(); notifications.show({ color: 'green', message: t('kiosk.disabled') }); },
    onError: kioskErr,
  });

  // ── Shartnoma rekvizitlari (hujjatdagi MAKTAB tomoni) ──
  // Ruxsat kiosk bilan bir xil qoida: backend `/contracts/requisites` STAFF talab qiladi,
  // platforma superadminida esa tenant konteksti yo'q (u doim 400 oladi).
  const canManageRequisites = canManageKiosk;

  const {
    data: req, isLoading: reqLoading, isError: reqIsError, refetch: refetchReq, isFetching: reqFetching,
  } = useQuery({
    queryKey: ['contract-requisites'],
    queryFn: async () => (await api.get<RequisitesResponse>('/contracts/requisites')).data,
    enabled: canManageRequisites,
    meta: SILENT_ERROR, // xato bo'limning o'zida ochiq ko'rsatiladi
  });

  // Maydonlar RO'YXATI serverdan olinadi — backend yangi maydon qo'shsa forma o'zi kengayadi.
  const requiredKeys = req?.requiredKeys?.length ? req.requiredKeys : REQUIRED_FALLBACK;
  const optionalKeys = req?.optionalKeys?.length ? req.optionalKeys : OPTIONAL_FALLBACK;

  const reqForm = useForm<Record<string, string>>({ initialValues: {} });

  // Server qiymatlari kelganda formaga solamiz (javob har yangilanganda bir marta).
  useEffect(() => {
    if (!req) return;
    const values: Record<string, string> = {};
    for (const k of [...requiredKeys, ...optionalKeys]) values[k] = req.requisites?.[k] ?? '';
    reqForm.setValues(values);
    reqForm.resetDirty(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);

  const reqMut = useMutation({
    mutationFn: async (values: Record<string, string>) =>
      (await api.put<RequisitesResponse & { tenantName?: string | null }>('/contracts/requisites', { requisites: values })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contract-requisites'] });
      // Maktab nomi = rekvizitdagi nom (backend Tenant.name ni ham yangilaydi) —
      // panel sarlavhasi qayta kirishsiz darhol yangi nomni ko'rsatsin
      if (data?.tenantName) updateUser({ tenantName: data.tenantName });
      notifications.show({ color: 'green', message: t('requisites.saved') });
    },
    onError: (e: unknown) => {
      // Backend qaysi maydon bo'shligini aytadi — xatoni AYNAN shu maydonlarga qo'yamiz
      const missing = (e as { response?: { data?: { missing?: string[] } } })?.response?.data?.missing;
      if (missing?.length) {
        reqForm.setErrors(Object.fromEntries(missing.map((k) => [k, t('requisites.required')])));
      }
      notifications.show({ color: 'red', message: errMsg(e, t('requisites.saveFailed')) });
    },
  });

  /** Saqlashdan oldin: bo'shliklarni kesamiz va majburiy maydonlarni tekshiramiz. */
  const saveRequisites = (values: Record<string, string>) => {
    const trimmed: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) trimmed[k] = (v ?? '').trim();
    const missing = requiredKeys.filter((k) => !trimmed[k]);
    if (missing.length) {
      reqForm.setErrors(Object.fromEntries(missing.map((k) => [k, t('requisites.required')])));
      return;
    }
    reqMut.mutate(trimmed);
  };

  /** Bitta rekvizit maydoni (manzil — ko'p qatorli). */
  const requisiteField = (k: string, optional = false) => {
    const label = t(`requisites.f.${k}`, k);
    const input = reqForm.getInputProps(k);
    const props = {
      label: optional ? `${label} ${t('requisites.optional')}` : label,
      description: DESCRIBED_KEYS.includes(k) ? t(`requisites.d.${k}`, '') || undefined : undefined,
      maxLength: REQUISITE_MAX_LEN,
      required: !optional,
      ...input,
      // Maydonlar ro'yxati SERVERDAN keladi — hali formaga tushmagan kalit uchun `value`
      // `undefined` bo'lib, input nazoratsizdan nazoratlanuvchiga sakrab ogohlantirish berardi.
      value: typeof input.value === 'string' ? input.value : '',
    };
    return k === 'address'
      ? <Textarea key={k} autosize minRows={2} {...props} />
      : <TextInput key={k} {...props} />;
  };

  const confirmRegenerate = () => modals.openConfirmModal({
    title: t('kiosk.regenerateTitle'),
    centered: true,
    children: <Text size="sm">{t('kiosk.regenerateWarning')}</Text>,
    labels: { confirm: t('kiosk.regenerate'), cancel: t('common.cancel') },
    confirmProps: { color: 'orange' },
    onConfirm: () => kioskKeyMut.mutate('regenerate'),
  });
  const confirmDisable = () => modals.openConfirmModal({
    title: t('kiosk.disableTitle'),
    centered: true,
    children: <Text size="sm">{t('kiosk.disableWarning')}</Text>,
    labels: { confirm: t('kiosk.disable'), cancel: t('common.cancel') },
    confirmProps: { color: 'red' },
    onConfirm: () => kioskDelMut.mutate(),
  });

  return (
    <>
      <PageHeader title={t('settings.title')} />

      <SectionCard title={t('settings.profile')} icon={IconUser} maw={520} mb="md"
        right={canEditProfile ? (
          <Button size="xs" variant="light" leftSection={<IconPencil size={14} />} onClick={openProfile}>
            {t('settings.editProfile')}
          </Button>
        ) : undefined}>
        <Group mb="md">
          <Avatar src={fileUrl(user?.avatar)} color="brand" radius="xl" size={72}>
            {user?.fullName?.[0]?.toUpperCase() ?? 'U'}
          </Avatar>
          <Stack gap={6}>
            <Text fw={500} size="sm">{t('settings.avatar')}</Text>
            <Group gap="xs">
              <FileButton onChange={(f) => f && avatarMut.mutate(f)} accept="image/png,image/jpeg,image/webp">
                {(props) => (
                  <Button {...props} size="xs" variant="light" loading={avatarMut.isPending}>
                    {t('settings.uploadAvatar')}
                  </Button>
                )}
              </FileButton>
              {user?.avatar && (
                <Button size="xs" variant="subtle" color="red" loading={avatarDelMut.isPending}
                  onClick={() => avatarDelMut.mutate()}>
                  {t('settings.removeAvatar')}
                </Button>
              )}
            </Group>
          </Stack>
        </Group>
        <Stack gap="xs">
          <Group justify="space-between"><Text c="dimmed" size="sm">{t('settings.fullName')}</Text><Text fw={500}>{user?.fullName}</Text></Group>
          <Group justify="space-between"><Text c="dimmed" size="sm">{t('settings.login')}</Text><Text ff="monospace">{user?.login}</Text></Group>
          <Group justify="space-between"><Text c="dimmed" size="sm">{t('settings.role')}</Text><Text>{t(`roles.${user?.role}`)}</Text></Group>
        </Stack>
      </SectionCard>

      <Modal opened={profileOpen} onClose={() => setProfileOpen(false)} title={t('settings.editProfile')} centered>
        <form onSubmit={profileForm.onSubmit((v) => profileMut.mutate(v))} noValidate>
          <Stack>
            <TextInput label={t('settings.fullName')} required maxLength={FULL_NAME_MAX} {...profileForm.getInputProps('fullName')} />
            <TextInput label={t('settings.login')} required autoComplete="username"
              styles={{ input: { fontFamily: 'monospace' } }}
              description={t('settings.loginDesc')} {...profileForm.getInputProps('login')} />
            {loginChanged && (
              <PasswordInput label={t('settings.oldPassword')} required autoComplete="current-password"
                description={t('settings.currentPasswordDesc')} {...profileForm.getInputProps('currentPassword')} />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setProfileOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={profileMut.isPending} leftSection={<IconDeviceFloppy size={16} />}>
                {t('common.save')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <SectionCard title={t('settings.language')} icon={IconLanguage} maw={520} mb="md">
        <SegmentedControl
          fullWidth
          value={(i18n.language?.slice(0, 2) as Lang) || 'uz'}
          onChange={(v) => setLanguage(v as Lang)}
          data={[
            { value: 'uz', label: "O'zbekcha" },
            { value: 'ru', label: 'Русский' },
            { value: 'en', label: 'English' },
          ]}
        />
      </SectionCard>

      {/* Ilova foydalanuvchilarining akkauntni o'chirish so'rovlari (Google Play talabi) — direktor/admin */}
      {isTenantStaff(user) && <AccountDeletionRequests />}

      {/* Kiosk ekrani — kirish yo'lagidagi TV uchun ochiq havola */}
      {canManageKiosk && (
        <SectionCard title={t('kiosk.section')} subtitle={t('kiosk.sectionDesc')} icon={IconDeviceTv} maw={520} mb="md">
          {kioskLoading ? (
            <Center py="md"><Loader size="sm" /></Center>
          ) : kioskIsError ? (
            <Stack gap="sm" align="flex-start">
              <Text size="sm" c="red">{t('kiosk.loadError')}</Text>
              <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />}
                loading={kioskFetching} onClick={() => void refetchKiosk()}>
                {t('kiosk.retry')}
              </Button>
            </Stack>
          ) : kiosk?.kioskKey ? (
            <Stack gap="xs">
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <TextInput style={{ flex: 1 }} label={t('kiosk.linkLabel')} readOnly value={kioskUrl}
                  onFocus={(e) => e.currentTarget.select()} />
                <CopyButton value={kioskUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? t('kiosk.copied') : t('kiosk.copy')} withArrow>
                      <ActionIcon variant="light" size="lg" color={copied ? 'teal' : 'brand'} onClick={copy}>
                        {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
                <Tooltip label={t('kiosk.open')} withArrow>
                  <ActionIcon variant="light" size="lg" component="a" href={kioskUrl} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Text size="xs" c="dimmed">{t('kiosk.linkHint')}</Text>
              <Group gap="xs" mt={4}>
                <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
                  loading={kioskKeyMut.isPending} onClick={confirmRegenerate}>
                  {t('kiosk.regenerate')}
                </Button>
                <Button size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />}
                  loading={kioskDelMut.isPending} onClick={confirmDisable}>
                  {t('kiosk.disable')}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack gap="sm" align="flex-start">
              <Text size="sm" c="dimmed">{t('kiosk.noLink')}</Text>
              <Button size="xs" leftSection={<IconDeviceTv size={16} />} loading={kioskKeyMut.isPending}
                onClick={() => kioskKeyMut.mutate('create')}>
                {t('kiosk.create')}
              </Button>
            </Stack>
          )}
        </SectionCard>
      )}

      {/* Shartnoma rekvizitlari — bu bo'lim to'ldirilmasa maktab shartnoma YARATA OLMAYDI
          (backend `resolveRequisites` 400 beradi). Shuning uchun holat ochiq ko'rsatiladi. */}
      {canManageRequisites && (
        <SectionCard
          title={t('requisites.section')}
          subtitle={t('requisites.sectionDesc')}
          icon={IconFileText}
          maw={520}
          mb="md"
          id="requisites"
        >
          {reqLoading ? (
            <Center py="md"><Loader size="sm" /></Center>
          ) : reqIsError ? (
            <Stack gap="sm" align="flex-start">
              <Text size="sm" c="red">{t('requisites.loadError')}</Text>
              <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />}
                loading={reqFetching} onClick={() => void refetchReq()}>
                {t('requisites.retry')}
              </Button>
            </Stack>
          ) : (
            // `noValidate`: brauzerning o'z "Please fill out this field" oynachasi emas,
            // bizning i18n xatolarimiz ko'rsatilsin (u oynacha panel tilida bo'lmaydi).
            <form onSubmit={reqForm.onSubmit(saveRequisites)} noValidate>
              <Stack gap="sm">
                {!req?.complete ? (
                  <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title={t('requisites.incomplete')}>
                    {t('requisites.incompleteHint')}
                  </Alert>
                ) : req?.isDefault ? (
                  <Alert color="blue" variant="light" title={t('requisites.unsaved')}>
                    {t('requisites.unsavedHint')}
                  </Alert>
                ) : null}

                {requiredKeys.map((k) => requisiteField(k))}

                <Divider my={4} label={t('requisites.optionalSection')} labelPosition="center" />
                {optionalKeys.map((k) => requisiteField(k, true))}

                <Group justify="flex-end" mt="xs">
                  <Button type="submit" loading={reqMut.isPending} leftSection={<IconDeviceFloppy size={16} />}>
                    {t('common.save')}
                  </Button>
                </Group>
              </Stack>
            </form>
          )}
        </SectionCard>
      )}

      {/* Support email — huquqiy hujjatlardagi "Savollar uchun" qatori (panel + ilova). */}
      {canManageSupportEmail && <SupportEmailSettings />}

      {/* Qarz hisobi boshlangan oy — undan oldingi oylar qarzga hisoblanmaydi (backend STAFF) */}
      {canManageSupportEmail && <DebtStartSettings />}

      {/* Maosh qoidasi — ish vaqti bo'yicha hisob (backend STAFF) */}
      {canManageSupportEmail && <PayrollRuleSettings />}

      {/* Ish kunlari — davomat va maosh hisobiga BIRDEK ta'sir qiladi, shuning
          uchun moliya yoki davomat sahifasida emas, umumiy sozlamalarda. */}
      {canManageWorkDays && <WorkDaysSettings />}
      {canManageWorkDays && <LessonTimeSettings />}

      <SectionCard title={t('settings.changePassword')} icon={IconLock} maw={520}>
        <form onSubmit={form.onSubmit((v) => changeMut.mutate({ oldPassword: v.oldPassword, newPassword: v.newPassword }))}>
          <Stack>
            <PasswordInput label={t('settings.oldPassword')} required {...form.getInputProps('oldPassword')} />
            <PasswordInput label={t('settings.newPassword')} required {...form.getInputProps('newPassword')} />
            <PasswordInput label={t('settings.confirm')} required {...form.getInputProps('confirm')} />
            <Group justify="flex-end">
              <Button type="submit" loading={changeMut.isPending}>{t('settings.change')}</Button>
            </Group>
          </Stack>
        </form>
      </SectionCard>

      {/* Huquqiy hujjatlar */}
      {/* Zaxira — ataylab ENG PASTDA va yig'ilgan holatda. Faqat direktor:
          bot tokeni qo'lga tushsa maktabning butun ma'lumoti chiqib ketishi mumkin. */}
      {(user?.role === 'DIRECTOR' || user?.role === 'SUPER_ADMIN') && <BackupSettings />}

      <SectionCard title={t('legal.section')} icon={IconScale} maw={520} mt="md">
        <Stack gap="xs">
          <Anchor component={Link} to="/terms" size="sm">{t('legal.terms')}</Anchor>
          <Anchor component={Link} to="/privacy" size="sm">{t('legal.privacy')}</Anchor>
          <Anchor component={Link} to="/cookies" size="sm">{t('legal.cookies')}</Anchor>
        </Stack>
      </SectionCard>
    </>
  );
}
